/**
 * A sheet: a sparse column index, the row/column geometry overrides, and the
 * incrementally tracked used range.
 *
 * Sparsity runs all the way down. A column that was never written has no entry
 * in `columns`, a 65,536-row span that was never written has no chunk, and both
 * facts are exploited by the navigation helpers: `blockEdge` walks the *chunk
 * index* rather than individual rows, so `Ctrl+Down` on an empty column costs
 * O(chunks) (at most 77 map lookups) instead of O(5,000,000) (Requirements 3.2,
 * 3.3, Property 11).
 *
 * The used range is maintained incrementally. A write that fills a previously
 * empty cell widens the bounding box in O(1). A clear that empties a cell on the
 * boundary cannot shrink the box without looking at the data, so it only marks
 * the box stale; the next `usedBounds` call recomputes it by walking the chunk
 * index of each non-empty column. Interior clears cost O(1) and leave the box
 * exact (Requirement 3.5, Property 12).
 *
 * Bounds checks always precede mutation, so a rejected write leaves no trace
 * (Requirements 1.2, 16.1, Property 2).
 */

import { LimitError } from '../errors.js';
import {
  CHUNK_ROWS,
  EMPTY,
  MAX_COLS,
  MAX_ROWS,
  normalizeRange,
  type Addr,
  type CellValue,
  type Direction,
  type Range,
  type ScalarType,
  type SheetId,
} from '../types.js';
import {
  chunkIndexOfRow,
  firstRowOfChunk,
  getKind,
  getPayload,
  getStyleId,
  KIND_BOOL,
  KIND_EMPTY,
  KIND_ERROR,
  KIND_NUMBER,
  KIND_TEXT,
  localRowOf,
  type Chunk,
  type FormulaCell,
} from './chunk.js';
import {
  chunkOfRow,
  clearFormulaAt,
  clearValue,
  columnCellCount,
  columnMemory,
  createColumn,
  declareColumnType as storeDeclareColumnType,
  deleteColumnRows,
  getCellStyleId,
  getFormulaAt,
  getValue,
  insertColumnRows,
  setCellStyleId,
  setFormulaAt,
  setValue,
  type Column,
} from './column.js';
import { stringOf } from './dictionary.js';

/** Default column width in CSS pixels (Requirement 1.6). */
export const DEFAULT_COL_WIDTH_PX = 100;
/** Default row height in CSS pixels (Requirement 1.7). */
export const DEFAULT_ROW_HEIGHT_PX = 20;

/**
 * Largest tile a single `getTile` call may materialize. A viewport plus its
 * 50-row / 20-column buffer is three orders of magnitude smaller; the cap exists
 * so a mistaken whole-sheet request is rejected instead of attempting a
 * multi-gigabyte allocation (Requirements 1.4, 16.1).
 */
export const MAX_TILE_CELLS = 1_048_576;

/** Highest chunk index that can hold a valid row. */
const LAST_CHUNK_INDEX = chunkIndexOfRow(MAX_ROWS - 1);

/**
 * A column filter. `cond` stays `unknown` until the Query_Engine defines
 * `FilterCondition`; the container exists now so `SheetMeta` matches the
 * workbook model and survives save/load unchanged.
 */
export interface SheetFilter {
  col: number;
  cond: unknown;
}

/** Per-sheet metadata, as persisted in the workbook manifest. */
export interface SheetMeta {
  id: SheetId;
  name: string;
  /** Tab order, 0-based and contiguous across the workbook. */
  index: number;
  /** Sparse width overrides; absent columns use {@link DEFAULT_COL_WIDTH_PX}. */
  colWidths: Map<number, number>;
  /** Sparse height overrides; absent rows use {@link DEFAULT_ROW_HEIGHT_PX}. */
  rowHeights: Map<number, number>;
  /** Freeze origin: rows above and columns left of it stay fixed (Requirement 1.8). */
  freeze: Addr | null;
  /** Populated by the Query_Engine. */
  filters: SheetFilter[];
  /** Populated by the Format_Manager. */
  merges: Range[];
  /** 1 + the last non-empty row, or 0 when the sheet is empty. */
  usedRows: number;
  /** 1 + the last non-empty column, or 0 when the sheet is empty. */
  usedCols: number;
}

/** Bounding box of the non-empty cells, plus the bookkeeping that keeps it cheap. */
export interface UsedBounds {
  minRow: number;
  minCol: number;
  maxRow: number;
  maxCol: number;
  /** Non-empty cell count. Exact at all times. */
  cells: number;
}

export interface Sheet {
  meta: SheetMeta;
  /** Sparse: an absent column means every cell in it is empty. */
  columns: Map<number, Column>;
  /** Internal bounding-box state; read through {@link usedBounds}. */
  bounds: UsedBounds;
  /** True when a boundary clear may have shrunk the box. */
  boundsStale: boolean;
}

/** Aggregated memory accounting (Requirement 14.6, Property 59). */
export interface StoreMemory {
  chunkCount: number;
  /** Rows made addressable by allocated chunks. */
  allocatedCells: number;
  /** Typed-array bytes. */
  cellBytes: number;
  /** Fixed per-chunk bookkeeping. */
  overheadBytes: number;
  /** Sparse formula and error-metadata bytes. */
  sparseBytes: number;
  /** Retained string-dictionary bytes. */
  dictBytes: number;
  totalBytes: number;
}

/**
 * A flat, transferable snapshot of a rectangular region.
 *
 * There are no per-cell objects: `kinds` tags each cell, `numbers` carries
 * numeric values (and 0/1 for booleans), `codes` carries either an index into
 * `strings` for text cells or an error ordinal for error cells, and `styleIds`
 * carries the style id. Indexing is row-major: `(row - r0) * cols + (col - c0)`.
 */
export interface TileSnapshot {
  /** The normalized range this tile covers. */
  range: Range;
  rows: number;
  cols: number;
  kinds: Uint8Array;
  numbers: Float64Array;
  /** Text: index into {@link TileSnapshot.strings}. Error: error ordinal. Else -1. */
  codes: Int32Array;
  strings: string[];
  styleIds: Int32Array;
}

export function createSheetData(id: SheetId, name: string, index: number): Sheet {
  return {
    meta: {
      id,
      name,
      index,
      colWidths: new Map(),
      rowHeights: new Map(),
      freeze: null,
      filters: [],
      merges: [],
      usedRows: 0,
      usedCols: 0,
    },
    columns: new Map(),
    bounds: { minRow: 0, minCol: 0, maxRow: -1, maxCol: -1, cells: 0 },
    boundsStale: false,
  };
}

/* ------------------------------------------------------------------ bounds */

export function checkRowBound(row: number): void {
  if (!Number.isInteger(row) || row < 0) throw new RangeError(`row out of range: ${row}`);
  if (row >= MAX_ROWS) throw new LimitError(MAX_ROWS, row + 1, 'rows', 'row beyond the sheet limit');
}

export function checkColBound(col: number): void {
  if (!Number.isInteger(col) || col < 0) throw new RangeError(`column out of range: ${col}`);
  if (col >= MAX_COLS) {
    throw new LimitError(MAX_COLS, col + 1, 'columns', 'column beyond the sheet limit');
  }
}

/** Validates both corners of a range before any mutation touches the sheet. */
export function checkRangeBounds(r: Range): Range {
  const n = normalizeRange(r);
  checkRowBound(n.r0);
  checkRowBound(n.r1);
  checkColBound(n.c0);
  checkColBound(n.c1);
  return n;
}

/** Highest local row of `chunkIndex` that is still a valid sheet row. */
function maxLocalOfChunk(chunkIndex: number): number {
  return Math.min(CHUNK_ROWS - 1, MAX_ROWS - 1 - firstRowOfChunk(chunkIndex));
}

function kindAt(column: Column | undefined, row: number): number {
  if (column === undefined) return KIND_EMPTY;
  const chunk = chunkOfRow(column, row);
  return chunk === undefined ? KIND_EMPTY : getKind(chunk, localRowOf(row));
}

function noteFilled(sheet: Sheet, row: number, col: number): void {
  const b = sheet.bounds;
  if (b.cells === 0) {
    b.minRow = row;
    b.maxRow = row;
    b.minCol = col;
    b.maxCol = col;
    b.cells = 1;
    sheet.boundsStale = false;
    return;
  }
  b.cells += 1;
  if (row < b.minRow) b.minRow = row;
  if (row > b.maxRow) b.maxRow = row;
  if (col < b.minCol) b.minCol = col;
  if (col > b.maxCol) b.maxCol = col;
}

function noteCleared(sheet: Sheet, row: number, col: number): void {
  const b = sheet.bounds;
  b.cells -= 1;
  if (b.cells <= 0) {
    sheet.bounds = { minRow: 0, minCol: 0, maxRow: -1, maxCol: -1, cells: 0 };
    sheet.boundsStale = false;
    return;
  }
  // Only a clear on the boundary can shrink the box, and shrinking needs data.
  if (row === b.minRow || row === b.maxRow || col === b.minCol || col === b.maxCol) {
    sheet.boundsStale = true;
  }
}

/** First non-empty row of `column`, walking its chunk index. */
function firstNonEmptyRowOf(column: Column): number | null {
  const keys = [...column.chunks.keys()].sort((a, b) => a - b);
  for (const key of keys) {
    const chunk = column.chunks.get(key);
    if (chunk === undefined || chunk.nonEmpty === 0) continue;
    const local = scanChunkForKind(chunk, 0, 1, maxLocalOfChunk(key), false);
    if (local !== null) return firstRowOfChunk(key) + local;
  }
  return null;
}

/** Last non-empty row of `column`, walking its chunk index in reverse. */
function lastNonEmptyRowOf(column: Column): number | null {
  const keys = [...column.chunks.keys()].sort((a, b) => b - a);
  for (const key of keys) {
    const chunk = column.chunks.get(key);
    if (chunk === undefined || chunk.nonEmpty === 0) continue;
    const local = scanChunkForKind(chunk, maxLocalOfChunk(key), -1, maxLocalOfChunk(key), false);
    if (local !== null) return firstRowOfChunk(key) + local;
  }
  return null;
}

/**
 * Scans one chunk from `startLocal` in `step` direction for the first row whose
 * emptiness matches `wantEmpty`. Returns the local row, or `null`.
 */
function scanChunkForKind(
  chunk: Chunk,
  startLocal: number,
  step: 1 | -1,
  maxLocal: number,
  wantEmpty: boolean,
): number | null {
  for (let local = startLocal; local >= 0 && local <= maxLocal; local += step) {
    const empty = (chunk.kinds[local] ?? KIND_EMPTY) === KIND_EMPTY;
    if (empty === wantEmpty) return local;
  }
  return null;
}

function recomputeBounds(sheet: Sheet): void {
  let minRow = Number.POSITIVE_INFINITY;
  let maxRow = -1;
  let minCol = Number.POSITIVE_INFINITY;
  let maxCol = -1;
  let cells = 0;

  for (const [colIndex, column] of sheet.columns) {
    const count = columnCellCount(column);
    if (count === 0) continue;
    const first = firstNonEmptyRowOf(column);
    const last = lastNonEmptyRowOf(column);
    if (first === null || last === null) continue;
    cells += count;
    if (colIndex < minCol) minCol = colIndex;
    if (colIndex > maxCol) maxCol = colIndex;
    if (first < minRow) minRow = first;
    if (last > maxRow) maxRow = last;
  }

  sheet.bounds =
    cells === 0
      ? { minRow: 0, minCol: 0, maxRow: -1, maxCol: -1, cells: 0 }
      : { minRow, minCol, maxRow, maxCol, cells };
  sheet.boundsStale = false;
}

/** The current bounding box, recomputing it first when a boundary clear made it stale. */
export function usedBounds(sheet: Sheet): UsedBounds {
  if (sheet.boundsStale) recomputeBounds(sheet);
  const b = sheet.bounds;
  sheet.meta.usedRows = b.cells === 0 ? 0 : b.maxRow + 1;
  sheet.meta.usedCols = b.cells === 0 ? 0 : b.maxCol + 1;
  return b;
}

/**
 * Bounding box of the non-empty cells, or `A1` when the sheet is empty
 * (Requirement 3.5, Property 12).
 */
export function usedRange(sheet: Sheet): Range {
  const b = usedBounds(sheet);
  if (b.cells === 0) return { sheet: sheet.meta.id, r0: 0, c0: 0, r1: 0, c1: 0 };
  return { sheet: sheet.meta.id, r0: b.minRow, c0: b.minCol, r1: b.maxRow, c1: b.maxCol };
}

/**
 * The bounding box of every cell holding *anything*: a value, a formula, or a
 * non-default style. `null` when the sheet is completely untouched.
 *
 * Wider than {@link usedRange}, which tracks values only, and needed because
 * formatting outlives content: a user can bold an empty cell, and a command that
 * deletes the row it sits in has to record that style in order to undo itself.
 * Cost is proportional to the allocated chunks, not to the sheet.
 */
export function contentRange(sheet: Sheet): Range | null {
  let minRow = Number.POSITIVE_INFINITY;
  let minCol = Number.POSITIVE_INFINITY;
  let maxRow = -1;
  let maxCol = -1;

  const grow = (row0: number, row1: number, col: number): void => {
    if (row0 < minRow) minRow = row0;
    if (row1 > maxRow) maxRow = row1;
    if (col < minCol) minCol = col;
    if (col > maxCol) maxCol = col;
  };

  const values = usedBounds(sheet);
  if (values.cells > 0) {
    grow(values.minRow, values.maxRow, values.minCol);
    grow(values.minRow, values.maxRow, values.maxCol);
  }

  for (const [col, column] of sheet.columns) {
    for (const [chunkIndex, chunk] of column.chunks) {
      const base = firstRowOfChunk(chunkIndex);
      const ids = chunk.styleIds;
      if (ids === undefined) {
        // Uniform style: either the whole chunk span is styled, or none of it is.
        if (chunk.uniformStyleId !== 0) grow(base, base + CHUNK_ROWS - 1, col);
      } else {
        for (let local = 0; local < ids.length; local++) {
          if (ids[local] !== 0) grow(base + local, base + local, col);
        }
      }
      const formulas = chunk.formulas;
      if (formulas !== undefined) {
        for (const local of formulas.keys()) grow(base + local, base + local, col);
      }
    }
  }

  if (maxRow < 0 || maxCol < 0) return null;
  return { sheet: sheet.meta.id, r0: minRow, c0: minCol, r1: maxRow, c1: maxCol };
}

/* ------------------------------------------------------------------ values */

export function getCell(sheet: Sheet, row: number, col: number): CellValue {
  checkRowBound(row);
  checkColBound(col);
  const column = sheet.columns.get(col);
  return column === undefined ? EMPTY : getValue(column, row);
}

export function setCell(sheet: Sheet, row: number, col: number, value: CellValue): void {
  checkRowBound(row);
  checkColBound(col);
  writeCell(sheet, row, col, value);
}

export function ensureColumn(sheet: Sheet, col: number): Column {
  checkColBound(col);
  let column = sheet.columns.get(col);
  if (column === undefined) {
    column = createColumn();
    sheet.columns.set(col, column);
  }
  return column;
}

/** Unchecked write. Callers must have validated the address already. */
function writeCell(sheet: Sheet, row: number, col: number, value: CellValue): void {
  if (value.k === 'empty') {
    clearCell(sheet, row, col);
    return;
  }
  const column = ensureColumn(sheet, col);
  const wasEmpty = kindAt(column, row) === KIND_EMPTY;
  setValue(column, row, value);
  if (wasEmpty) noteFilled(sheet, row, col);
}

/** Clears one cell's value, keeping its style (Requirement 2.8). */
function clearCell(sheet: Sheet, row: number, col: number): void {
  const column = sheet.columns.get(col);
  if (column === undefined) return;
  const wasNonEmpty = kindAt(column, row) !== KIND_EMPTY;
  clearValue(column, row);
  if (wasNonEmpty) noteCleared(sheet, row, col);
}

/**
 * Bulk write. `values` must match the range exactly, and the shape is checked
 * before the first cell is touched so a mis-shaped paste changes nothing.
 */
export function setRegion(sheet: Sheet, range: Range, values: readonly CellValue[][]): void {
  const r = checkRangeBounds(range);
  const rows = r.r1 - r.r0 + 1;
  const cols = r.c1 - r.c0 + 1;
  if (values.length !== rows) {
    throw new RangeError(`setRegion: expected ${rows} rows of values, received ${values.length}`);
  }
  for (let i = 0; i < rows; i++) {
    const rowValues = values[i];
    if (rowValues === undefined || rowValues.length !== cols) {
      throw new RangeError(
        `setRegion: row ${i} has ${rowValues?.length ?? 0} values, expected ${cols}`,
      );
    }
  }
  for (let i = 0; i < rows; i++) {
    const rowValues = values[i] as CellValue[];
    for (let j = 0; j < cols; j++) {
      writeCell(sheet, r.r0 + i, r.c0 + j, rowValues[j] ?? EMPTY);
    }
  }
}

/**
 * Clears every value in `range` while retaining formatting (Requirement 2.8).
 *
 * Only allocated data is visited: absent columns and absent chunks are skipped
 * wholesale, so clearing `A1:XFD5000000` on a sparse sheet costs O(non-empty).
 */
export function clearValues(sheet: Sheet, range: Range): void {
  const r = checkRangeBounds(range);
  for (const [colIndex, column] of sheet.columns) {
    if (colIndex < r.c0 || colIndex > r.c1) continue;
    let row: number | null = nextNonEmptyRow(column, r.r0, 1);
    while (row !== null && row <= r.r1) {
      clearCell(sheet, row, colIndex);
      row = nextNonEmptyRow(column, row + 1, 1);
    }
    clearFormulasIn(column, r.r0, r.r1);
  }
}

/** Drops formula records whose cached value was already empty. */
function clearFormulasIn(column: Column, r0: number, r1: number): void {
  const first = chunkIndexOfRow(r0);
  const last = chunkIndexOfRow(r1);
  for (let idx = first; idx <= last; idx++) {
    const chunk = column.chunks.get(idx);
    const formulas = chunk?.formulas;
    if (formulas === undefined) continue;
    const base = firstRowOfChunk(idx);
    for (const local of [...formulas.keys()]) {
      const row = base + local;
      if (row >= r0 && row <= r1) clearFormulaAt(column, row);
    }
  }
}

/* ---------------------------------------------------------------- formulas */

/**
 * The formula record stored at one cell, or `undefined` when the cell holds a
 * literal. The record carries the user's source string, an optional cached
 * syntax tree, and the value the last Recalculation_Cycle produced.
 */
export function getFormula(sheet: Sheet, row: number, col: number): FormulaCell | undefined {
  checkRowBound(row);
  checkColBound(col);
  const column = sheet.columns.get(col);
  return column === undefined ? undefined : getFormulaAt(column, row);
}

/**
 * Stores a formula and its cached value together. The cached value goes through
 * the same write path as a literal, so the used range and the column type stay
 * correct and readers never special-case formula cells.
 */
export function setFormula(sheet: Sheet, row: number, col: number, cell: FormulaCell): void {
  checkRowBound(row);
  checkColBound(col);
  let column = sheet.columns.get(col);
  if (column === undefined) {
    column = createColumn();
    sheet.columns.set(col, column);
  }
  // One write, not two: `setFormulaAt` stores the cached value through the same
  // path a literal takes, so the used-range bookkeeping is all that is left to
  // do here. A recalculation of 100,000 cells passes through this line 100,000
  // times (Requirement 4.6), so the redundant write is worth avoiding.
  const wasEmpty = kindAt(column, row) === KIND_EMPTY;
  setFormulaAt(column, row, cell);
  const isEmpty = cell.cached.k === 'empty';
  if (wasEmpty && !isEmpty) noteFilled(sheet, row, col);
  else if (!wasEmpty && isEmpty) noteCleared(sheet, row, col);
}

/** Drops the formula record at one cell, leaving its last value in place. */
export function clearFormula(sheet: Sheet, row: number, col: number): void {
  checkRowBound(row);
  checkColBound(col);
  const column = sheet.columns.get(col);
  if (column !== undefined) clearFormulaAt(column, row);
}

/**
 * Every cell on the sheet that holds a formula, in column-major chunk order.
 *
 * Only allocated chunks that actually carry a formula map are visited, so a
 * sheet of five million literals costs O(chunks) rather than O(cells). The
 * Formula_Engine uses this to rewire the dependency graph after a structural
 * edit (Requirement 4.13).
 */
export function formulaCells(sheet: Sheet): Addr[] {
  const out: Addr[] = [];
  for (const [colIndex, column] of sheet.columns) {
    for (const [chunkIndex, chunk] of column.chunks) {
      const formulas = chunk.formulas;
      if (formulas === undefined || formulas.size === 0) continue;
      const base = firstRowOfChunk(chunkIndex);
      for (const local of formulas.keys()) {
        out.push({ sheet: sheet.meta.id, row: base + local, col: colIndex });
      }
    }
  }
  return out;
}

export function columnType(sheet: Sheet, col: number): ScalarType {
  checkColBound(col);
  return sheet.columns.get(col)?.type ?? 'empty';
}

/**
 * Asserts an inferred column type (Requirement 10.7).
 *
 * The Import_Service calls this once per column when it commits: the type it
 * inferred from its samples, confirmed against the stored kinds. The column keeps
 * the declaration only while the stored data agrees with it, so a declaration can
 * never make {@link columnType} lie about what is actually in the column.
 */
export function declareColumnType(sheet: Sheet, col: number, type: ScalarType): void {
  checkColBound(col);
  const column = sheet.columns.get(col);
  if (column === undefined) return;
  storeDeclareColumnType(column, type);
}

/* -------------------------------------------------------------- sheet data */

/**
 * The value-bearing half of a sheet: its sparse column index and the bounding-box
 * bookkeeping that goes with it.
 *
 * Separated from {@link SheetMeta} because the Import_Service commits a whole
 * table at once (Requirement 10.2). Staging parses into a detached sheet and the
 * commit swaps this half into the target, which is O(columns) rather than
 * O(cells): a 5,000,000-row import must not be copied twice, and the target's
 * name, tab position, widths, heights, and freeze must survive the import.
 */
export interface SheetData {
  columns: Map<number, Column>;
  bounds: UsedBounds;
  boundsStale: boolean;
}

/** The sheet's current value half, by reference. */
export function readSheetData(sheet: Sheet): SheetData {
  return { columns: sheet.columns, bounds: sheet.bounds, boundsStale: sheet.boundsStale };
}

/**
 * Replaces the sheet's value half and returns what was there, so the caller can
 * record it as an inverse patch (Requirement 12.4).
 *
 * Metadata is untouched; only the cached used-range numbers are refreshed, since
 * they describe the data that just changed.
 */
export function writeSheetData(sheet: Sheet, data: SheetData): SheetData {
  const previous = readSheetData(sheet);
  sheet.columns = data.columns;
  sheet.bounds = data.bounds;
  sheet.boundsStale = data.boundsStale;
  usedBounds(sheet);
  return previous;
}

/** Style id of one cell. The Format_Manager owns the style table itself. */
export function cellStyleId(sheet: Sheet, row: number, col: number): number {
  checkRowBound(row);
  checkColBound(col);
  const column = sheet.columns.get(col);
  return column === undefined ? 0 : getCellStyleId(column, row);
}

export function setCellStyle(sheet: Sheet, row: number, col: number, styleId: number): void {
  checkRowBound(row);
  checkColBound(col);
  if (!Number.isInteger(styleId) || styleId < 0) {
    throw new RangeError(`style id must be a non-negative integer: ${styleId}`);
  }
  let column = sheet.columns.get(col);
  if (column === undefined) {
    if (styleId === 0) return;
    column = createColumn();
    sheet.columns.set(col, column);
  }
  setCellStyleId(column, row, styleId);
}

/* ------------------------------------------------------------------- tiles */

export function tileOf(sheet: Sheet, range: Range): TileSnapshot {
  const r = checkRangeBounds(range);
  const rows = r.r1 - r.r0 + 1;
  const cols = r.c1 - r.c0 + 1;
  const total = rows * cols;
  if (total > MAX_TILE_CELLS) {
    throw new LimitError(MAX_TILE_CELLS, total, 'cells', 'tile request too large');
  }

  const kinds = new Uint8Array(total);
  const numbers = new Float64Array(total);
  const codes = new Int32Array(total).fill(-1);
  const styleIds = new Int32Array(total);
  const strings: string[] = [];
  const stringIndex = new Map<string, number>();

  for (let col = r.c0; col <= r.c1; col++) {
    const column = sheet.columns.get(col);
    if (column === undefined) continue;
    const dict = column.dict;
    const colOffset = col - r.c0;
    let row = r.r0;
    while (row <= r.r1) {
      const idx = chunkIndexOfRow(row);
      const lastRowOfChunk = Math.min(r.r1, firstRowOfChunk(idx) + CHUNK_ROWS - 1);
      const chunk = column.chunks.get(idx);
      if (chunk === undefined) {
        row = lastRowOfChunk + 1;
        continue;
      }
      for (let rr = row; rr <= lastRowOfChunk; rr++) {
        const local = localRowOf(rr);
        const at = (rr - r.r0) * cols + colOffset;
        styleIds[at] = getStyleId(chunk, local);
        const kind = getKind(chunk, local);
        if (kind === KIND_EMPTY) continue;
        kinds[at] = kind;
        switch (kind) {
          case KIND_NUMBER:
            numbers[at] = getPayload(chunk, local);
            break;
          case KIND_BOOL:
            numbers[at] = getPayload(chunk, local) === 0 ? 0 : 1;
            break;
          case KIND_ERROR:
            codes[at] = getPayload(chunk, local);
            break;
          case KIND_TEXT: {
            const text = dict === undefined ? '' : stringOf(dict, getPayload(chunk, local));
            let si = stringIndex.get(text);
            if (si === undefined) {
              si = strings.length;
              strings.push(text);
              stringIndex.set(text, si);
            }
            codes[at] = si;
            break;
          }
        }
      }
      row = lastRowOfChunk + 1;
    }
  }

  return { range: r, rows, cols, kinds, numbers, codes, strings, styleIds };
}

/* -------------------------------------------------------------- navigation */

/**
 * First non-empty row at or after (`step` 1) / at or before (`step` -1) `from`.
 * Walks the chunk index, so unallocated spans cost one map lookup each.
 */
function nextNonEmptyRow(column: Column | undefined, from: number, step: 1 | -1): number | null {
  if (column === undefined || column.chunks.size === 0) return null;
  if (from < 0 || from >= MAX_ROWS) return null;
  const startIdx = chunkIndexOfRow(from);
  for (
    let idx = startIdx;
    idx >= 0 && idx <= LAST_CHUNK_INDEX;
    idx += step
  ) {
    const chunk = column.chunks.get(idx);
    if (chunk === undefined || chunk.nonEmpty === 0) continue;
    const maxLocal = maxLocalOfChunk(idx);
    const startLocal = idx === startIdx ? localRowOf(from) : step === 1 ? 0 : maxLocal;
    const local = scanChunkForKind(chunk, startLocal, step, maxLocal, false);
    if (local !== null) return firstRowOfChunk(idx) + local;
  }
  return null;
}

/** First empty row at or after / at or before `from`, treating absent chunks as empty. */
function nextEmptyRow(column: Column | undefined, from: number, step: 1 | -1): number | null {
  if (from < 0 || from >= MAX_ROWS) return null;
  const startIdx = chunkIndexOfRow(from);
  for (
    let idx = startIdx;
    idx >= 0 && idx <= LAST_CHUNK_INDEX;
    idx += step
  ) {
    const maxLocal = maxLocalOfChunk(idx);
    const startLocal = idx === startIdx ? localRowOf(from) : step === 1 ? 0 : maxLocal;
    const chunk = column?.chunks.get(idx);
    if (chunk === undefined || chunk.nonEmpty === 0) return firstRowOfChunk(idx) + startLocal;
    const local = scanChunkForKind(chunk, startLocal, step, maxLocal, true);
    if (local !== null) return firstRowOfChunk(idx) + local;
  }
  return null;
}

/** Present column indices in scan order, filtered to the side of `from`. */
function candidateCols(sheet: Sheet, from: number, step: 1 | -1): number[] {
  const keys: number[] = [];
  for (const key of sheet.columns.keys()) {
    if (step === 1 ? key >= from : key <= from) keys.push(key);
  }
  return keys.sort((a, b) => (step === 1 ? a - b : b - a));
}

function nextNonEmptyCol(sheet: Sheet, row: number, from: number, step: 1 | -1): number | null {
  if (from < 0 || from >= MAX_COLS) return null;
  for (const col of candidateCols(sheet, from, step)) {
    if (kindAt(sheet.columns.get(col), row) !== KIND_EMPTY) return col;
  }
  return null;
}

function nextEmptyCol(sheet: Sheet, row: number, from: number, step: 1 | -1): number | null {
  for (let col = from; col >= 0 && col < MAX_COLS; col += step) {
    if (kindAt(sheet.columns.get(col), row) === KIND_EMPTY) return col;
  }
  return null;
}

/**
 * `Ctrl+Arrow` target (Requirements 3.2, 3.3).
 *
 * Excel semantics: inside a run of non-empty cells, jump to the last cell of the
 * run; otherwise jump across the gap to the next non-empty cell; when nothing
 * lies ahead, jump to the sheet edge.
 */
export function blockEdge(sheet: Sheet, row: number, col: number, dir: Direction): Addr {
  checkRowBound(row);
  checkColBound(col);
  const here = { sheet: sheet.meta.id, row, col };

  if (dir === 'down' || dir === 'up') {
    const step: 1 | -1 = dir === 'down' ? 1 : -1;
    const edge = dir === 'down' ? MAX_ROWS - 1 : 0;
    if (row === edge) return here;
    const column = sheet.columns.get(col);
    const next = row + step;
    const inRun =
      kindAt(column, row) !== KIND_EMPTY && kindAt(column, next) !== KIND_EMPTY;
    if (inRun) {
      const firstEmpty = nextEmptyRow(column, next, step);
      return { ...here, row: firstEmpty === null ? edge : firstEmpty - step };
    }
    const filled = nextNonEmptyRow(column, next, step);
    return { ...here, row: filled ?? edge };
  }

  const step: 1 | -1 = dir === 'right' ? 1 : -1;
  const edge = dir === 'right' ? MAX_COLS - 1 : 0;
  if (col === edge) return here;
  const next = col + step;
  const inRun =
    kindAt(sheet.columns.get(col), row) !== KIND_EMPTY &&
    kindAt(sheet.columns.get(next), row) !== KIND_EMPTY;
  if (inRun) {
    const firstEmpty = nextEmptyCol(sheet, row, next, step);
    return { ...here, col: firstEmpty === null ? edge : firstEmpty - step };
  }
  const filled = nextNonEmptyCol(sheet, row, next, step);
  return { ...here, col: filled ?? edge };
}

/* ---------------------------------------------------------------- geometry */

export function colWidth(sheet: Sheet, col: number): number {
  checkColBound(col);
  return sheet.meta.colWidths.get(col) ?? DEFAULT_COL_WIDTH_PX;
}

export function rowHeight(sheet: Sheet, row: number): number {
  checkRowBound(row);
  return sheet.meta.rowHeights.get(row) ?? DEFAULT_ROW_HEIGHT_PX;
}

function checkPixels(px: number, what: string): void {
  if (!Number.isFinite(px) || px < 0) {
    throw new RangeError(`${what} must be a finite non-negative number of pixels: ${px}`);
  }
}

export function setColWidth(sheet: Sheet, col: number, px: number): void {
  checkColBound(col);
  checkPixels(px, 'column width');
  if (px === DEFAULT_COL_WIDTH_PX) sheet.meta.colWidths.delete(col);
  else sheet.meta.colWidths.set(col, px);
}

export function setRowHeight(sheet: Sheet, row: number, px: number): void {
  checkRowBound(row);
  checkPixels(px, 'row height');
  if (px === DEFAULT_ROW_HEIGHT_PX) sheet.meta.rowHeights.delete(row);
  else sheet.meta.rowHeights.set(row, px);
}

/** Sets or clears the freeze origin (Requirement 1.8). */
export function freezePane(sheet: Sheet, at: Addr | null): void {
  if (at === null) {
    sheet.meta.freeze = null;
    return;
  }
  checkRowBound(at.row);
  checkColBound(at.col);
  sheet.meta.freeze = { sheet: sheet.meta.id, row: at.row, col: at.col };
}

/* ------------------------------------------------------- structural edits */

/** Axis a structural edit operates on. */
export type StructuralAxis = 'rows' | 'columns';

function axisLimit(axis: StructuralAxis): number {
  return axis === 'rows' ? MAX_ROWS : MAX_COLS;
}

function axisNoun(axis: StructuralAxis): string {
  return axis === 'rows' ? 'row' : 'column';
}

function checkStructuralArgs(at: number, count: number, axis: StructuralAxis): void {
  if (!Number.isInteger(at) || at < 0) throw new RangeError(`${axisNoun(axis)} out of range: ${at}`);
  if (!Number.isInteger(count) || count < 1) {
    throw new RangeError(`${axis} count must be a positive integer: ${count}`);
  }
  const limit = axisLimit(axis);
  if (at >= limit) throw new LimitError(limit, at + 1, axis, `${axisNoun(axis)} beyond the sheet limit`);
  if (at + count > limit) {
    throw new LimitError(limit, at + count, axis, `${axis} beyond the sheet limit`);
  }
}

/**
 * Rejects an insertion that would push a non-empty cell off the end of the
 * sheet. The check runs before anything moves, so a rejected insertion leaves
 * the sheet unchanged rather than silently losing data (Requirement 16.1).
 */
function checkInsertHeadroom(sheet: Sheet, at: number, count: number, axis: StructuralAxis): void {
  const b = usedBounds(sheet);
  if (b.cells === 0) return;
  const last = axis === 'rows' ? b.maxRow : b.maxCol;
  const limit = axisLimit(axis);
  if (last >= at && last + count >= limit) {
    throw new LimitError(
      limit,
      last + count + 1,
      axis,
      `insertion would push content past the ${axisNoun(axis)} limit`,
    );
  }
}

/** Where index `i` lands once `count` slots are inserted at `at`. */
function afterInsert(i: number, at: number, count: number, limit: number): number {
  return i < at ? i : Math.min(i + count, limit - 1);
}

/** Where index `i` lands once `count` slots are deleted at `at`; `null` when it is removed. */
function afterDelete(i: number, at: number, count: number): number | null {
  if (i < at) return i;
  if (i < at + count) return null;
  return i - count;
}

/** Re-keys a sparse geometry override map across an insertion. */
function shiftKeysForInsert<T>(
  map: Map<number, T>,
  at: number,
  count: number,
  limit: number,
): Map<number, T> {
  const next = new Map<number, T>();
  for (const [key, value] of map) {
    const moved = key < at ? key : key + count;
    if (moved < limit) next.set(moved, value);
  }
  return next;
}

/** Re-keys a sparse geometry override map across a deletion. */
function shiftKeysForDelete<T>(map: Map<number, T>, at: number, count: number): Map<number, T> {
  const next = new Map<number, T>();
  for (const [key, value] of map) {
    const moved = afterDelete(key, at, count);
    if (moved !== null) next.set(moved, value);
  }
  return next;
}

/**
 * Re-maps the merged regions. A region straddling the insertion point grows by
 * `count`; a region whose whole span is deleted disappears, and one that
 * collapses to a single cell stops being a merge (Requirement 6.8).
 */
function shiftMerges(
  merges: readonly Range[],
  at: number,
  count: number,
  axis: StructuralAxis,
  inserting: boolean,
): Range[] {
  const limit = axisLimit(axis);
  const out: Range[] = [];
  for (const raw of merges) {
    const m = normalizeRange(raw);
    const from = axis === 'rows' ? m.r0 : m.c0;
    const to = axis === 'rows' ? m.r1 : m.c1;
    let lo: number;
    let hi: number;
    if (inserting) {
      lo = afterInsert(from, at, count, limit);
      hi = afterInsert(to, at, count, limit);
    } else {
      lo = afterDelete(from, at, count) ?? at;
      hi = afterDelete(to, at, count) ?? at - 1;
    }
    if (hi < lo) continue;
    const moved: Range = axis === 'rows' ? { ...m, r0: lo, r1: hi } : { ...m, c0: lo, c1: hi };
    if (moved.r0 === moved.r1 && moved.c0 === moved.c1) continue;
    out.push(moved);
  }
  return out;
}

/** Moves the freeze origin with the data it was anchored to (Requirement 1.8). */
function shiftFreeze(
  freeze: Addr | null,
  at: number,
  count: number,
  axis: StructuralAxis,
  inserting: boolean,
): Addr | null {
  if (freeze === null) return null;
  const i = axis === 'rows' ? freeze.row : freeze.col;
  const moved = inserting
    ? afterInsert(i, at, count, axisLimit(axis))
    : (afterDelete(i, at, count) ?? at);
  return axis === 'rows' ? { ...freeze, row: moved } : { ...freeze, col: moved };
}

/** Moves column filters with their columns, dropping filters on deleted columns. */
function shiftFilters(
  filters: readonly SheetFilter[],
  at: number,
  count: number,
  inserting: boolean,
): SheetFilter[] {
  const out: SheetFilter[] = [];
  for (const f of filters) {
    if (inserting) {
      const moved = f.col < at ? f.col : f.col + count;
      if (moved < MAX_COLS) out.push({ ...f, col: moved });
    } else {
      const moved = afterDelete(f.col, at, count);
      if (moved !== null) out.push({ ...f, col: moved });
    }
  }
  return out;
}

/**
 * Forces a bounding-box recomputation and refreshes the cached `usedRows` /
 * `usedCols`. A structural edit moves an unbounded amount of content, so the
 * incremental bookkeeping cannot be patched up in O(1); the recomputation walks
 * the chunk index only (Requirement 3.5, Property 12).
 */
function invalidateBounds(sheet: Sheet): void {
  sheet.boundsStale = true;
  usedBounds(sheet);
}

/**
 * Inserts `count` blank rows at `at`, shifting the rows at or after `at` down
 * together with their values, formulas, styles, row heights, merges, and the
 * freeze origin (Requirement 4.13).
 *
 * Empty column containers are kept rather than pruned, so a column keeps the
 * scalar type the importer declared for it even when a deletion empties it.
 */
export function insertRows(sheet: Sheet, at: number, count: number): void {
  checkStructuralArgs(at, count, 'rows');
  checkInsertHeadroom(sheet, at, count, 'rows');

  for (const column of sheet.columns.values()) insertColumnRows(column, at, count);
  sheet.meta.rowHeights = shiftKeysForInsert(sheet.meta.rowHeights, at, count, MAX_ROWS);
  sheet.meta.merges = shiftMerges(sheet.meta.merges, at, count, 'rows', true);
  sheet.meta.freeze = shiftFreeze(sheet.meta.freeze, at, count, 'rows', true);
  invalidateBounds(sheet);
}

/** Deletes the `count` rows at `at`, pulling everything below them up. */
export function deleteRows(sheet: Sheet, at: number, count: number): void {
  checkStructuralArgs(at, count, 'rows');

  for (const column of sheet.columns.values()) deleteColumnRows(column, at, count);
  sheet.meta.rowHeights = shiftKeysForDelete(sheet.meta.rowHeights, at, count);
  sheet.meta.merges = shiftMerges(sheet.meta.merges, at, count, 'rows', false);
  sheet.meta.freeze = shiftFreeze(sheet.meta.freeze, at, count, 'rows', false);
  invalidateBounds(sheet);
}

/**
 * Inserts `count` blank columns at `at`. Columns are held in a sparse map, so
 * the shift is a re-keying rather than a data move (Requirement 4.13).
 */
export function insertCols(sheet: Sheet, at: number, count: number): void {
  checkStructuralArgs(at, count, 'columns');
  checkInsertHeadroom(sheet, at, count, 'columns');

  const moved = new Map<number, Column>();
  for (const [index, column] of sheet.columns) {
    const target = index < at ? index : index + count;
    if (target < MAX_COLS) moved.set(target, column);
  }
  sheet.columns = moved;
  sheet.meta.colWidths = shiftKeysForInsert(sheet.meta.colWidths, at, count, MAX_COLS);
  sheet.meta.merges = shiftMerges(sheet.meta.merges, at, count, 'columns', true);
  sheet.meta.freeze = shiftFreeze(sheet.meta.freeze, at, count, 'columns', true);
  sheet.meta.filters = shiftFilters(sheet.meta.filters, at, count, true);
  invalidateBounds(sheet);
}

/** Deletes the `count` columns at `at`, pulling everything to their right left. */
export function deleteCols(sheet: Sheet, at: number, count: number): void {
  checkStructuralArgs(at, count, 'columns');

  const moved = new Map<number, Column>();
  for (const [index, column] of sheet.columns) {
    const target = afterDelete(index, at, count);
    if (target !== null) moved.set(target, column);
  }
  sheet.columns = moved;
  sheet.meta.colWidths = shiftKeysForDelete(sheet.meta.colWidths, at, count);
  sheet.meta.merges = shiftMerges(sheet.meta.merges, at, count, 'columns', false);
  sheet.meta.freeze = shiftFreeze(sheet.meta.freeze, at, count, 'columns', false);
  sheet.meta.filters = shiftFilters(sheet.meta.filters, at, count, false);
  invalidateBounds(sheet);
}

/* ------------------------------------------------------------------ memory */

export function sheetMemory(sheet: Sheet): StoreMemory {
  const total: StoreMemory = {
    chunkCount: 0,
    allocatedCells: 0,
    cellBytes: 0,
    overheadBytes: 0,
    sparseBytes: 0,
    dictBytes: 0,
    totalBytes: 0,
  };
  for (const column of sheet.columns.values()) {
    const m = columnMemory(column);
    total.chunkCount += m.chunkCount;
    total.allocatedCells += m.allocatedCells;
    total.cellBytes += m.cellBytes;
    total.overheadBytes += m.overheadBytes;
    total.sparseBytes += m.sparseBytes;
    total.dictBytes += m.dictBytes;
    total.totalBytes += m.totalBytes;
  }
  return total;
}
